import type {
  CollectionLoanAccount,
  CollectionSavingsAccount,
} from '@/api/collections-types'
import type { AttendanceStatus, GroupDetail, GroupMeeting, GroupMember } from '@/api/types'
import type { Pesewas } from '@/lib/money'

import {
  canTakeDeposit,
  isLiveRound,
  SAVINGS_STATUS_EXPLANATION,
  type CollectionColumn,
} from './constants'
import type { CollectionPermissions } from './permissions'

/**
 * The shape of a collection sheet, derived from three contracts that do not know about each other.
 *
 * Everything in this file is a pure function of what the server said. Nothing here decides whether
 * a posting is allowed — the server does that on every line — but it decides what an operator is
 * shown *before* they key forty amounts, which is the difference between a refusal caught on the
 * sheet and a refusal caught on line nine of a batch that has already posted eight.
 */

/**
 * Why a cell has no amount field.
 *
 * The distinction the rest of the screen turns on is between `restricted` and `none`, and it is
 * not a nicety. `restricted` means the server withheld the answer from this reader — the accounts
 * may well exist and somebody else can see them. `none` means the institution never captured one:
 * this member has no savings account, or no round is open. Rendering them the same way would tell
 * an officer a member has no loan when in fact the officer simply may not read loans, and the
 * member would go home without paying.
 */
export type CellAvailability =
  | { kind: 'available' }
  /** The read has not answered yet. Not an answer, and not to be shown as an absence. */
  | { kind: 'pending' }
  /** The reader does not hold the permission that would let the server answer. */
  | { kind: 'restricted'; permission: string }
  /** The read was attempted and failed. Not an answer, and not to be shown as an empty one. */
  | { kind: 'unreadable' }
  /** The read succeeded and there is genuinely nothing to post against. */
  | { kind: 'none'; explanation: string }
  /** It exists, and cannot take money today. The reason is what the server would refuse with. */
  | { kind: 'blocked'; explanation: string }
  /** The reader may see the account but not post to it. */
  | { kind: 'readOnly'; permission: string }

/** Where one column of one row would post, once an amount is keyed into it. */
export interface SheetTarget {
  /** The loan round's id, or the savings account's id. */
  id: string
  /** What an operator would write on the paper sheet — a round reference or an account number. */
  reference: string
  currencyCode: string
  /**
   * The most the server will accept, where there is one. A repayment above the round's outstanding
   * balance is refused as `loans.repayment_exceeds_outstanding`; a deposit has no ceiling at all,
   * so this is null for the two savings columns.
   */
  ceilingMinor: number | null
  /** The balance to show beside the field: what is owed, or what is held. */
  contextMinor: number
  /** A second figure worth seeing on a loan row: what is already overdue. */
  arrearsMinor: number | null
  /** Days past due, for a round in arrears. */
  daysInArrears: number | null
}

export interface SheetCell {
  column: CollectionColumn
  availability: CellAvailability
  target: SheetTarget | null
}

export interface SheetRow {
  /**
   * The line's number on the paper sheet — the row's position in the register, one-based.
   *
   * It is the only trail from a ledger entry back to a line of paper somebody is holding, so it is
   * carried into every narration this screen writes.
   */
  lineNumber: number
  customerId: string
  customerNumber: string
  fullName: string
  /** How the member was marked at this meeting, or null where the register is not yet recorded. */
  attendance: AttendanceStatus | null
  cells: Record<CollectionColumn, SheetCell>
}

/** The keyed amounts, by row and column. `null` is an empty field, which is not zero. */
export type SheetAmounts = Record<string, Pesewas | null>

export const amountKey = (customerId: string, column: CollectionColumn) => `${customerId}:${column}`

export const readAmount = (
  amounts: SheetAmounts,
  customerId: string,
  column: CollectionColumn,
): Pesewas | null => amounts[amountKey(customerId, column)] ?? null

/**
 * Who could have been at this meeting, and therefore whose line belongs on the sheet.
 *
 * Membership is dated and a sheet is a record of one day: somebody who joined last week was not at
 * last month's meeting, and somebody who left in March was at February's. Shaping the sheet to the
 * meeting's date rather than to today's membership is what keeps a transcribed sheet matching the
 * paper it came from.
 */
export function membersOnDate(members: GroupMember[], date: string): GroupMember[] {
  return members
    .filter(
      (member) => member.joinedDate <= date && (member.leftDate === null || member.leftDate >= date),
    )
    .sort((left, right) => left.fullName.localeCompare(right.fullName))
}

export interface SheetSources {
  group: GroupDetail
  meeting: GroupMeeting
  /** Loan accounts written under this group, or null where they could not be read. */
  loanAccounts: CollectionLoanAccount[] | null
  loansPending: boolean
  loansUnreadable: boolean
  /** One member's savings accounts, by customer id. A missing entry has not been read yet. */
  savingsByCustomer: Map<string, CollectionSavingsAccount[] | null>
  permissions: CollectionPermissions
}

function loanCell(
  member: GroupMember,
  sources: SheetSources,
  permissionCodes: { loanView: string; repaymentPost: string },
): SheetCell {
  if (!sources.permissions.canViewLoans) {
    return {
      column: 'LoanRepayment',
      availability: { kind: 'restricted', permission: permissionCodes.loanView },
      target: null,
    }
  }

  if (sources.loansUnreadable) {
    return { column: 'LoanRepayment', availability: { kind: 'unreadable' }, target: null }
  }

  if (sources.loansPending || sources.loanAccounts === null) {
    return { column: 'LoanRepayment', availability: { kind: 'pending' }, target: null }
  }

  // A member may hold more than one account under the same group — a closed one from last year
  // beside this year's. The one carrying a live round is the only one a repayment can reach, so it
  // is preferred over whichever the list happened to answer first; a closed account chosen by
  // accident would report "no open round" over a round that is open.
  const held = sources.loanAccounts.filter(
    (candidate) => candidate.customerId === member.customerId,
  )
  const account =
    held.find((candidate) => candidate.currentCycle !== null && isLiveRound(candidate.currentCycle.status)) ??
    held.find((candidate) => candidate.currentCycle !== null) ??
    held[0]

  if (!account) {
    return {
      column: 'LoanRepayment',
      availability: {
        kind: 'none',
        explanation: 'No loan account under this group. An individual loan is not collected here.',
      },
      target: null,
    }
  }

  const cycle = account.currentCycle
  if (cycle === null) {
    return {
      column: 'LoanRepayment',
      availability: {
        kind: 'none',
        explanation:
          account.status === 'Active'
            ? `Account ${account.accountNumber} has no open round, so there is nothing to repay.`
            : `Account ${account.accountNumber} is ${account.status.toLowerCase()} and carries no open round, so there is nothing to repay.`,
      },
      target: null,
    }
  }

  if (!isLiveRound(cycle.status)) {
    return {
      column: 'LoanRepayment',
      availability: {
        kind: 'blocked',
        explanation: `Round ${cycle.displayReference} is ${cycle.status.toLowerCase()}, not active. A repayment against it is refused as loans.not_active.`,
      },
      target: null,
    }
  }

  const target: SheetTarget = {
    id: cycle.id,
    reference: cycle.displayReference,
    currencyCode: cycle.currencyCode,
    ceilingMinor: cycle.totalOutstandingMinor,
    contextMinor: cycle.totalOutstandingMinor,
    arrearsMinor: cycle.amountInArrearsMinor,
    daysInArrears: cycle.daysInArrears,
  }

  if (!sources.permissions.canPostRepayment) {
    return {
      column: 'LoanRepayment',
      availability: { kind: 'readOnly', permission: permissionCodes.repaymentPost },
      target,
    }
  }

  return { column: 'LoanRepayment', availability: { kind: 'available' }, target }
}

function savingsCell(
  member: GroupMember,
  column: Extract<CollectionColumn, 'SavingsDeposit' | 'SecurityDeposit'>,
  sources: SheetSources,
  permissionCodes: { savingsView: string; post: string },
  canPost: boolean,
): SheetCell {
  if (!sources.permissions.canViewSavings) {
    return {
      column,
      availability: { kind: 'restricted', permission: permissionCodes.savingsView },
      target: null,
    }
  }

  const accounts = sources.savingsByCustomer.get(member.customerId)

  // Absent from the map means the read has not answered yet; present and null means it answered
  // with a failure. The two are rendered differently, because "still loading" and "could not be
  // read" ask an operator to do different things.
  if (accounts === undefined) {
    return { column, availability: { kind: 'pending' }, target: null }
  }

  if (accounts === null) {
    return { column, availability: { kind: 'unreadable' }, target: null }
  }

  const wanted = column === 'SavingsDeposit' ? 'RegularSavings' : 'SecurityDeposit'
  const matching = accounts.filter((account) => account.productClass === wanted)

  if (matching.length === 0) {
    return {
      column,
      availability: {
        kind: 'none',
        explanation:
          column === 'SavingsDeposit'
            ? 'This member holds no savings account. One must be opened before savings can be taken.'
            : 'This member holds no security deposit account. Cash collateral has nowhere to go.',
      },
      target: null,
    }
  }

  // An open account first: a member may hold a closed account beside a live one, and the live one
  // is the only one a deposit can reach.
  const account = matching.find((candidate) => canTakeDeposit(candidate.status)) ?? matching[0]

  if (!canTakeDeposit(account.status)) {
    return {
      column,
      availability: {
        kind: 'blocked',
        explanation: `${account.accountNumber} — ${SAVINGS_STATUS_EXPLANATION[account.status]}`,
      },
      target: null,
    }
  }

  const target: SheetTarget = {
    id: account.id,
    reference: account.accountNumber,
    currencyCode: account.currencyCode,
    // A deposit has no ceiling. The account can always take more money.
    ceilingMinor: null,
    contextMinor: account.ledgerBalanceMinor,
    arrearsMinor: null,
    daysInArrears: null,
  }

  if (!canPost) {
    return { column, availability: { kind: 'readOnly', permission: permissionCodes.post }, target }
  }

  return { column, availability: { kind: 'available' }, target }
}

export interface BuildSheetOptions extends SheetSources {
  permissionCodes: {
    loanView: string
    repaymentPost: string
    savingsView: string
    depositPost: string
    securityDepositTopUp: string
  }
}

/** The whole sheet: one row per member who belonged to the group on the meeting's date. */
export function buildSheet(options: BuildSheetOptions): SheetRow[] {
  const eligible = membersOnDate(options.group.members, options.meeting.scheduledDate)
  const marks = new Map(
    options.meeting.attendance.map((entry) => [entry.customerId, entry.status] as const),
  )

  return eligible.map((member, index) => ({
    lineNumber: index + 1,
    customerId: member.customerId,
    customerNumber: member.customerNumber,
    fullName: member.fullName,
    attendance: marks.get(member.customerId) ?? null,
    cells: {
      LoanRepayment: loanCell(member, options, {
        loanView: options.permissionCodes.loanView,
        repaymentPost: options.permissionCodes.repaymentPost,
      }),
      SavingsDeposit: savingsCell(
        member,
        'SavingsDeposit',
        options,
        {
          savingsView: options.permissionCodes.savingsView,
          post: options.permissionCodes.depositPost,
        },
        options.permissions.canPostDeposit,
      ),
      SecurityDeposit: savingsCell(
        member,
        'SecurityDeposit',
        options,
        {
          savingsView: options.permissionCodes.savingsView,
          post: options.permissionCodes.securityDepositTopUp,
        },
        options.permissions.canTopUpSecurityDeposit,
      ),
    },
  }))
}

// -------------------------------------------------------------------------- totals and refusals

/** A line the sheet already knows the server will refuse, named before anything is posted. */
export interface SheetObjection {
  customerId: string
  column: CollectionColumn
  lineNumber: number
  memberName: string
  /** The stable code the server would answer with, so the wording and the log agree. */
  code: string
  message: string
}

/**
 * Everything wrong with the sheet as it stands.
 *
 * Only refusals this client can be certain of: an amount at or below zero, and a repayment above
 * the round's outstanding balance, which the server states exactly and refuses rather than
 * repricing. Anything the server decides on balances it reads in its own transaction is left to
 * the server — guessing at those would produce a screen that blocks work the API would have taken.
 */
export function objections(rows: SheetRow[], amounts: SheetAmounts): SheetObjection[] {
  const found: SheetObjection[] = []

  for (const row of rows) {
    for (const column of Object.keys(row.cells) as CollectionColumn[]) {
      const cell = row.cells[column]
      const amount = readAmount(amounts, row.customerId, column)

      if (amount === null) {
        continue
      }

      const shared = {
        customerId: row.customerId,
        column,
        lineNumber: row.lineNumber,
        memberName: row.fullName,
      }

      if (cell.target === null || cell.availability.kind !== 'available') {
        found.push({
          ...shared,
          code: 'collections.no_target',
          message: 'There is nowhere to post this amount. Clear the field.',
        })
        continue
      }

      if (amount <= 0) {
        found.push({
          ...shared,
          code:
            column === 'LoanRepayment' ? 'loans.repayment_not_positive' : 'savings.amount_not_positive',
          message: 'An amount must be greater than zero. Leave the field empty instead.',
        })
        continue
      }

      if (cell.target.ceilingMinor !== null && amount > cell.target.ceilingMinor) {
        found.push({
          ...shared,
          code: 'loans.repayment_exceeds_outstanding',
          message:
            'More than the round has outstanding. Post what clears the round and take the rest as savings.',
        })
      }
    }
  }

  return found
}

export interface SheetTotals {
  /** Per column, in integer minor units. */
  byColumn: Record<CollectionColumn, Pesewas>
  /** Every column added together — the figure checked against the officer's declared total. */
  capturedMinor: Pesewas
  /** How many amounts have been keyed. */
  lineCount: number
  /** How many members have at least one amount against them. */
  memberCount: number
}

/** The running total, summed in integer pesewas and never through a float. */
export function totalsFor(rows: SheetRow[], amounts: SheetAmounts): SheetTotals {
  const byColumn: Record<CollectionColumn, Pesewas> = {
    LoanRepayment: 0,
    SavingsDeposit: 0,
    SecurityDeposit: 0,
  }

  let lineCount = 0
  let memberCount = 0

  for (const row of rows) {
    let memberHasAmount = false

    for (const column of Object.keys(row.cells) as CollectionColumn[]) {
      const amount = readAmount(amounts, row.customerId, column)
      if (amount === null || amount === 0) {
        continue
      }

      byColumn[column] += amount
      lineCount += 1
      memberHasAmount = true
    }

    if (memberHasAmount) {
      memberCount += 1
    }
  }

  return {
    byColumn,
    capturedMinor:
      byColumn.LoanRepayment + byColumn.SavingsDeposit + byColumn.SecurityDeposit,
    lineCount,
    memberCount,
  }
}

/**
 * How many keyed amounts are still waiting to be sent.
 *
 * Not the same as the number of amounts on the sheet. A batch that stopped part-way leaves lines
 * already settled, and those are locked and excluded from the next run — the sheet's totals still
 * count them, because the sheet is the sheet, but there is nothing left to post against them.
 */
export function countPostableLines(
  rows: SheetRow[],
  amounts: SheetAmounts,
  postedKeys: ReadonlySet<string>,
): number {
  let count = 0

  for (const row of rows) {
    for (const column of Object.keys(row.cells) as CollectionColumn[]) {
      const amount = readAmount(amounts, row.customerId, column)

      if (
        amount !== null &&
        amount > 0 &&
        row.cells[column].availability.kind === 'available' &&
        !postedKeys.has(amountKey(row.customerId, column))
      ) {
        count += 1
      }
    }
  }

  return count
}
